import React, { useEffect, useState } from "react";
import { DatePicker, Input, Slider } from "antd";
import { GoDash } from "react-icons/go";
import Button from "./Button";
import MultiSelectDropdown from "./multiSelectDropdown";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GetUsersData } from "../redux/Auth/actions";
import dayjs from "dayjs";

const Filter = ({
  className,
  handleFilterSubmit,
  selectedId,
  setSelectedId,
  dateRange,
  setDateRange,
  setRange,
  range,
  selectedStatus,
  setSelectedStatus,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [filter, setFilter] = useState({
    page: 1,
    limit: 20,
    search: "",
  });
  const [hasMoreData, setHasMoreData] = useState(true);

  const usersData = useSelector((state) => state?.Auth?.usersData?.data);

  // Ensure usersData is defined and not empty
  const options = usersData
    ? usersData.map((e) => ({
        label: e?.store?.name ? e?.store?.name : e?.user?.name,
        value: e?._id,
      }))
    : [];

  const dateFormatList = ["DD/MM/YYYY", "DD/MM/YY", "DD-MM-YYYY", "DD-MM-YY"];

  const status = [
    {
      name: "Approval",
      value: "Approval",
    },
    {
      name: "Pending",
      value: "Pending",
    },
    {
      name: "All",
      value: "All",
    },
  ];

  const onSliderChange = (value) => {
    setRange(value);
  };

  const onInputChange = (index, value) => {
    const newRange = [...range];
    newRange[index] = value;
    setRange(newRange);
  };

  const fetchMoreData = () => {
    const newLimit = filter.limit + 10;
    setFilter((prevFilter) => ({ ...prevFilter, limit: newLimit }));
  };

  useEffect(() => {
    dispatch(
      GetUsersData({
        page: filter?.page,
        limit: filter?.limit,
        search: filter?.search,
      })
    ).then((res) => {
      if (res?.data?.length === 0) {
        setHasMoreData(false); // No more data to fetch
      }
      if (res.data?.status === 401) {
        navigate("/");
      }
    });
  }, [dispatch, navigate, filter, hasMoreData]);

  const onDateChange = (index, date) => {
    const newDateRange = [...dateRange];
    newDateRange[index] = date ? dayjs(date).format("DD/MM/YYYY") : null;
    setDateRange(newDateRange);
  };

  const handleStatusChange = (value) => {
    if (value === "Approval") {
      setSelectedStatus(true);
    } else if (value === "Pending") {
      setSelectedStatus(false);
    } else if (value === "All") {
      setSelectedStatus(null);
    }
  };

  const handleResetFilters = () => {
    setSelectedId([]); // Clear selected store IDs
    setDateRange([null, null]); // Reset date range
    setRange([0, 0]); // Reset range slider
    setSelectedStatus(null); // Reset status
  };

  return (
    <div className={`border rounded-[10px] bg-white ${className}`}>
      <div className="p-[15px] w-[370px] flex items-center justify-between border-b bg-bodyBg rounded-t-[10px] sticky top-0 z-50">
        <p className="text-lg font-medium">Filter</p>
        <p
          onClick={handleResetFilters}
          className="text-base font-normal text-green cursor-pointer"
        >
          Reset
        </p>
      </div>
      <div className="p-[15px] flex flex-col gap-5">
        <div className="flex flex-col gap-[10px]">
          <label className="text-sm font-medium text-textgray" htmlFor="select">
            Store
          </label>
          <MultiSelectDropdown
            options={options}
            className={"w-full"}
            setSelectedId={setSelectedId}
            selectedValues={selectedId}
            fetchMoreData={fetchMoreData} // Pass the function
            hasMoreData={hasMoreData}
          />
        </div>
        <hr />

        <div className="flex flex-col gap-[10px]">
          <label className="text-sm font-medium text-textgray" htmlFor="select">
            Date
          </label>
          <div className="flex items-center gap-[10px]">
            <DatePicker
              placeholder="Start date"
              className="h-[50px] w-full rounded-[10px] border border-[#e5e7eb] hover:border-[#e5e7eb] focus:outline-none"
              format={dateFormatList}
              onChange={(date) => onDateChange(0, date)}
              suffixIcon={<img src="/images/calenderIcon.svg" alt="" />}
              value={dateRange[0] ? dayjs(dateRange[0], "DD/MM/YYYY") : null}
            />
            <GoDash className="w-8" />
            <DatePicker
              placeholder="End date"
              className="h-[50px] w-full rounded-[10px] border border-[#e5e7eb] hover:border-[#e5e7eb] focus:outline-none"
              format={dateFormatList}
              onChange={(date) => onDateChange(1, date)}
              suffixIcon={<img src="/images/calenderIcon.svg" alt="" />}
              value={dateRange[1] ? dayjs(dateRange[1], "DD/MM/YYYY") : null}
            />
          </div>
        </div>
        <hr />

        <div className="flex flex-col gap-[10px]">
          <label className="text-sm font-medium text-textgray" htmlFor="select">
            Status
          </label>
          <div className="flex items-center gap-7">
            {status?.map((status, index) => {
              return (
                <div key={index} className="flex items-center gap-[10px]">
                  <input
                    type="radio"
                    name="status"
                    id="status"
                    readOnly
                    onChange={() => handleStatusChange(status.value)}
                    checked={
                      selectedStatus === null && status.value === "All"
                        ? true
                        : selectedStatus === true && status.value === "Approval"
                        ? true
                        : selectedStatus === false && status.value === "Pending"
                        ? true
                        : false
                    } // Add checked attribute to reset
                  />
                  <p>{status.name}</p>
                </div>
              );
            })}
          </div>
        </div>
        <hr />

        <div className="flex flex-col gap-[10px]">
          <label className="text-sm font-medium text-textgray" htmlFor="select">
            Total Amount
          </label>
          <Slider
            range
            defaultValue={range}
            value={range}
            onChange={onSliderChange}
            min={0}
            max={10000}
            className="custom-slider"
            tooltip={{
              formatter: (value) => `$${value}`,
              visible: true,
              placement: "bottom",
            }}
          />
          <div className="flex items-center gap-[10px]">
            <Input
              className="h-[50px]"
              type="number"
              value={range[0]}
              onChange={(e) => onInputChange(0, Number(e.target.value))}
              formatter={(value) => `$${value}`}
              parser={(value) => value.replace("$", "")}
              prefix={"$"}
            />
            <GoDash className="w-8" />
            <Input
              className="h-[50px]"
              type="number"
              value={range[1]}
              onChange={(e) => onInputChange(1, Number(e.target.value))}
              formatter={(value) => `$${value}`}
              parser={(value) => value.replace("$", "")}
              prefix={"$"}
            />
          </div>
        </div>

        <Button
          className={
            "border border-green text-green hover:bg-green hover:text-white w-full !rounded-[10px]"
          }
          children={"Apply"}
          onClick={handleFilterSubmit}
        />
      </div>
    </div>
  );
};

export default Filter;
